import type { FormProps } from '../../types'
import { useController } from 'react-hook-form'
import { cn } from '../../../lib/utils'

export interface PictureOption {
  value: string
  image: string
  fileId?: string
  description?: string
}

export interface CustomPictureChoiceInput {
  name: string
  type: string
  customControlType: 'picture-choice'
  value?: string
  campaignId?: string
  previewMode?: boolean
  questionnaireMode?: boolean
  options: PictureOption[]
  validation?: {
    required?: boolean
    minLength?: number
    maxLength?: number
    pattern?: string
    min?: number
    max?: number
    step?: number
  }
  uiOptions?: {
    testId?: string
  }
}

export default function CustomPictureChoice({
  field,
  formProps,
  previewMode,
  cardClassName = 'w-full',
}: {
  field: CustomPictureChoiceInput
  formProps: FormProps
  previewMode: boolean
  // Width class for each option card. Defaults to 'w-full' so the end-user
  // flow keeps its single-column layout; the builder preview overrides this to
  // a fixed width so the same-sized cards flow horizontally and wrap.
  cardClassName?: string
}) {
  const s3Url = import.meta.env.VITE_S3_URL

  const {
    field: { onChange, value },
  } = useController({
    control: formProps.control,
    name: field.name,
  })

  return (
    <>
      <div>
        <div className="flex flex-wrap gap-4">
          {field?.options?.map((option: PictureOption) => {
            const isSelected = value === option.value
            const imageSrc = `${s3Url}/${option.fileId}`

            return (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  !previewMode ? onChange(option.value) : undefined
                }
                className={cn(
                  cardClassName,
                  'overflow-hidden transition-all duration-200 rounded-lg',
                  'flex flex-col cursor-pointer',
                  isSelected
                    ? 'border-2 border-primary bg-primary text-primary-foreground'
                    : 'border-2 border-base-input bg-background-light text-slate-50',
                )}
              >
                {/* Image Container — fixed 2:1 box so a 2:1 (1200×600)
                    upload fills it with no crop or blur. */}
                <div className="relative w-full overflow-hidden group aspect-2/1">
                  <img
                    className="w-full h-full object-cover"
                    src={imageSrc}
                    alt={option?.description || 'option'}
                  />
                </div>

                {/* Text Container */}
                <div className="p-4 md:p-6 text-center">
                  <p className="md:text-lg font-medium">
                    {option?.description}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </>
  )
}
